import React from "react";

import { toAWSDate } from "@examspark/common/dist/utils/coursePlanner";
import {
  Button,
  ListTable,
  SelectInput,
  TextInput,
} from "@examspark/common-ui";
import {
  IconAddLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { AwsDateInput } from "@examspark/common-ui/dist/components/inputs/DateInput";
import { unwrapSetStateAction } from "@examspark/common-ui/dist/utils/react";
import { NonInstructionDate } from "@examspark/types";

import { HolidayWithId } from "./WorkspaceCalendarForm";

export const NonInstructionDateTable = ({
  holidays,
  onAddHoliday,
  onRemoveHoliday,
  onHolidayChange,
}: {
  holidays: HolidayWithId[];
  onAddHoliday: (holiday: NonInstructionDate) => void;
  onRemoveHoliday: (id: string) => void;
  onHolidayChange: (id: string, holiday: Partial<NonInstructionDate>) => void;
}) => {
  return (
    <ListTable>
      <caption>Non-Instructional Days</caption>

      {holidays?.length > 0 && (
        <thead>
          <tr>
            <th style={{ minWidth: "15ch" }}>Label</th>
            <th style={{ width: "25ch" }}>Type</th>
            <th style={{ width: "10ch" }}>Start </th>
            <th style={{ width: "10ch" }}>End </th>
            <th style={{ width: "4ch" }}></th>
          </tr>
        </thead>
      )}
      <tbody>
        {holidays?.map((holiday) => (
          <tr key={holiday.id}>
            <td>
              <TextInput
                fullWidth
                disabled={holiday.isDisabled}
                value={holiday.label ?? ""}
                onChange={(e) => {
                  onHolidayChange(holiday.id, {
                    label: e.target.value,
                  });
                }}
              />
            </td>

            <td>
              <SelectInput
                value={holiday.type ?? ""}
                disabled={holiday.isDisabled}
                onChange={(e) => {
                  onHolidayChange(holiday.id, {
                    type: e?.value,
                  });
                }}
                options={[
                  { value: "ASSESSMENT", label: "Assessment" },
                  { value: "HOLIDAY", label: "Holiday" },
                  { value: "TEACHER_WORKDAY", label: "Teacher Workday" },
                  { value: "OTHER", label: "Other" },
                ]}
              />
            </td>

            <td>
              <AwsDateInput
                disabled={holiday.isDisabled}
                date={holiday.startAt}
                onDateChange={(startAt) => {
                  // also change the endAt
                  onHolidayChange(holiday.id, {
                    startAt: unwrapSetStateAction(holiday.startAt, startAt),
                    endAt: unwrapSetStateAction(
                      holiday.endAt ?? holiday.startAt,
                      startAt,
                    ),
                  });
                }}
              />
            </td>

            <td>
              <AwsDateInput
                disabled={holiday.isDisabled}
                date={holiday.endAt ?? holiday.startAt}
                onDateChange={(endAt) => {
                  onHolidayChange(holiday.id, {
                    endAt: unwrapSetStateAction(
                      holiday.endAt ?? holiday.startAt,
                      endAt,
                    ),
                  });
                }}
                minDate={holiday.startAt}
              />
            </td>

            <td>
              <Button
                disabled={holiday.isDisabled}
                variant="plain"
                onClick={() => onRemoveHoliday(holiday.id)}
                title={"Remove Holiday"}
              >
                <IconTrashLine width="16" />
              </Button>
            </td>
          </tr>
        ))}

        <tr>
          <td colSpan={5}>
            <Button
              title="Add Non-Instructional Day"
              variant="outlined"
              onClick={() =>
                onAddHoliday({
                  label: "",
                  startAt: toAWSDate(new Date()),
                  endAt: toAWSDate(new Date()),
                  type: "HOLIDAY",
                })
              }
              icon={<IconAddLine width="12" />}
            >
              Add Non-Instructional Day
            </Button>
          </td>
        </tr>
      </tbody>
    </ListTable>
  );
};
